import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CountrySelect } from '@/components/CountrySelect';
import { useFideAutofill } from '@/lib/useFideAutofill';
import { FideAutofillDialog } from '@/components/fide/FideAutofillDialog';
import type { FideAutofillTarget } from '@/lib/fide-autofill';
import { countryFlagClass, getCountryName } from '@/lib/countries';
import { PlayerDetailFooter } from './PlayerDetailFooter';
import type { Player, PlayerStatus } from '@swiss-magic/shared-types';
import { BMAB_TITLES, FIDE_TITLES, getGameConfig } from '@swiss-magic/shared-types';
import { bmabTitleLabel, fideTitleLabel, ratingFieldLabel, externalIdFieldLabel } from '@/lib/game-labels';

export function PlayerDetailPanel({
  player,
  canEdit,
  isRegistrationOpen,
  onClose,
  onUpdate,
  onStatusChange,
  onRemove,
  onInvite,
  onRefreshFromProfile,
  onMessage,
  onUnblock,
  gameId,
}: {
  player: Player;
  canEdit: boolean;
  isRegistrationOpen: boolean;
  onClose: () => void;
  onUpdate: (player: Player, updates: Record<string, unknown>) => Promise<void>;
  onStatusChange: (player: Player, status: PlayerStatus) => void;
  onRemove: (player: Player) => void;
  onInvite: (player: Player) => void;
  onRefreshFromProfile: (player: Player) => void;
  onMessage: (player: Player) => void;
  onUnblock: (player: Player) => void;
  gameId: string;
}) {
  const { t, i18n } = useTranslation('director');
  const { t: tc } = useTranslation('common');
  const [editing, setEditing] = useState(false);

  // Story 098 — FIDE ID autofill from the FIDE ID entered on this card.
  const fideAutofill = useFideAutofill('director');
  function applyFideAutofill(v: Partial<FideAutofillTarget>) {
    setEditFields((prev) => ({ ...prev, ...v }));
  }
  function lookupFide() {
    void fideAutofill.lookup(
      editFields.fideId,
      {
        firstName: editFields.firstName,
        lastName: editFields.lastName,
        sex: editFields.sex,
        birthYear: editFields.birthYear,
        chessTitle: editFields.chessTitle,
        rating: editFields.rating,
        country: editFields.country,
      },
      applyFideAutofill,
    );
  }
  const [editFields, setEditFields] = useState({
    firstName: player.firstName,
    lastName: player.lastName,
    rating: player.rating != null ? String(player.rating) : '',
    fideId: player.fideId != null ? String(player.fideId) : '',
    city: player.city ?? '',
    country: player.country ?? '',
    club: player.club ?? '',
    bmabTitle: player.bmabTitle ?? '',
    sex: player.sex ?? '',
    birthYear: player.birthYear != null ? String(player.birthYear) : '',
    chessTitle: player.chessTitle ?? '',
    notes: player.notes ?? '',
  });

  function resetFields() {
    setEditFields({
      firstName: player.firstName,
      lastName: player.lastName,
      rating: player.rating != null ? String(player.rating) : '',
      fideId: player.fideId != null ? String(player.fideId) : '',
      city: player.city ?? '',
      country: player.country ?? '',
      club: player.club ?? '',
      bmabTitle: player.bmabTitle ?? '',
      sex: player.sex ?? '',
      birthYear: player.birthYear != null ? String(player.birthYear) : '',
      chessTitle: player.chessTitle ?? '',
      notes: player.notes ?? '',
    });
  }

  // Bug 2 — re-sync editFields when the parent swaps the player object
  // (refresh-from-profile, status flip, etc.). Triggers only on prop ref
  // changes, not on every render, since usePlayersController only calls
  // setSelectedPlayer with a new object on intentional updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { resetFields(); }, [player]);

  async function handleSave() {
    const updates: Record<string, unknown> = {};
    if (editFields.firstName !== player.firstName) updates.firstName = editFields.firstName.trim();
    if (editFields.lastName !== player.lastName) updates.lastName = editFields.lastName.trim();

    const newRating = editFields.rating ? Number(editFields.rating) : null;
    if (newRating !== player.rating) updates.rating = newRating;

    const newFideId = editFields.fideId ? Number(editFields.fideId) : null;
    if (newFideId !== player.fideId) updates.fideId = newFideId;

    const newCity = editFields.city.trim() || null;
    if (newCity !== player.city) updates.city = newCity;

    const newCountry = editFields.country.trim() || null;
    if (newCountry !== player.country) updates.country = newCountry;

    const newClub = editFields.club.trim() || null;
    if (newClub !== player.club) updates.club = newClub;

    if (gameId === 'backgammon') {
      const newBmab = editFields.bmabTitle || null;
      if (newBmab !== player.bmabTitle) updates.bmabTitle = newBmab;
    }

    // Story 096 — FIDE rating-grade fields (chess).
    if (gameId === 'chess') {
      const newSex = editFields.sex || null;
      if (newSex !== player.sex) updates.sex = newSex;
      const newBirthYear = editFields.birthYear ? Number(editFields.birthYear) : null;
      if (newBirthYear !== player.birthYear) updates.birthYear = newBirthYear;
      const newTitle = editFields.chessTitle || null;
      if (newTitle !== player.chessTitle) updates.chessTitle = newTitle;
    }

    const newNotes = editFields.notes.trim() || null;
    if (newNotes !== player.notes) updates.notes = newNotes;

    if (Object.keys(updates).length > 0) {
      await onUpdate(player, updates);
    }
    setEditing(false);
  }

  function handleCancel() {
    resetFields();
    setEditing(false);
  }

  function handleDelete() {
    // Parent decides whether to show a block-confirmation dialog (connected +
    // not-yet-blocked) or archive/delete outright (already blocked /
    // unconnected). Don't close the panel here — the parent controls that.
    onRemove(player);
  }

  const gameConfig = getGameConfig(gameId);
  const fields = [
    { key: 'firstName', label: t('players_page.table.first_name') },
    { key: 'lastName', label: t('players_page.table.last_name') },
    ...(gameConfig.playerFields.rating ? [{ key: 'rating', label: ratingFieldLabel(gameConfig, tc), type: 'number' }] : []),
    ...(gameConfig.playerFields.externalId ? [{ key: 'fideId', label: externalIdFieldLabel(gameConfig, tc), type: 'number' }] : []),
    { key: 'city', label: t('players_page.table.city') },
    { key: 'country', label: t('players_page.table.country') },
    { key: 'club', label: t('players_page.table.club') },
  ];
  const nonePlaceholder = t('players_page.detail_panel.none_placeholder');

  return (
    // Full-screen sheet on mobile (its original 020 design); on desktop a
    // centered modal over a dimmed backdrop — the full-screen takeover was
    // oversized for a registration card there. Backdrop click closes.
    <div
      className="fixed inset-0 z-50 bg-black/50 md:flex md:items-center md:justify-center md:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        data-testid="player-detail-panel"
        className="flex h-full w-full flex-col bg-background md:h-auto md:max-h-[85vh] md:w-full md:max-w-2xl md:rounded-lg md:border md:shadow-lg"
      >
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-lg font-semibold">
          {player.firstName} {player.lastName}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          data-testid="detail-close-button"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {fields.map(({ key, label, type }) => (
            <div key={key}>
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                {label}
              </label>
              {editing ? (
                key === 'country' ? (
                  <CountrySelect
                    data-testid={`detail-edit-${key}`}
                    value={editFields.country}
                    disabled={player.profileUserId != null}
                    onChange={(code) =>
                      setEditFields((prev) => ({ ...prev, country: code }))
                    }
                  />
                ) : (
                  <Input
                    data-testid={`detail-edit-${key}`}
                    type={type ?? 'text'}
                    value={editFields[key as keyof typeof editFields]}
                    disabled={player.profileUserId != null}
                    onChange={(e) =>
                      setEditFields((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                  />
                )
              ) : (
                <p
                  data-testid={`detail-${key}`}
                  className="rounded border bg-muted/30 px-3 py-2 text-sm"
                >
                  {key === 'country' && editFields.country
                    ? <><span className={countryFlagClass(editFields.country)} /> {getCountryName(editFields.country, i18n.language)}</>
                    : editFields[key as keyof typeof editFields] || nonePlaceholder}
                </p>
              )}
            </div>
          ))}

          {gameId === 'backgammon' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                {t('players_page.detail_panel.bmab_title_label')}
              </label>
              {editing ? (
                <select
                  data-testid="detail-edit-bmabTitle"
                  value={editFields.bmabTitle}
                  disabled={player.profileUserId != null}
                  onChange={(e) =>
                    setEditFields((prev) => ({
                      ...prev,
                      bmabTitle: e.target.value,
                    }))
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
                >
                  <option value="">{t('players_page.detail_panel.bmab_none_option')}</option>
                  <optgroup label={t('players_page.detail_panel.bmab_group_mastership')}>
                    {BMAB_TITLES.filter((tit) => tit.group === 'mastership').map((tit) => (
                      <option key={tit.code} value={tit.code}>
                        {bmabTitleLabel(tit.code, tit.label, tc)} ({tit.code})
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t('players_page.detail_panel.bmab_group_proficiency')}>
                    {BMAB_TITLES.filter((tit) => tit.group === 'proficiency').map((tit) => (
                      <option key={tit.code} value={tit.code}>
                        {bmabTitleLabel(tit.code, tit.label, tc)} ({tit.code})
                      </option>
                    ))}
                  </optgroup>
                </select>
              ) : (
                <p
                  data-testid="detail-bmabTitle"
                  className="rounded border bg-muted/30 px-3 py-2 text-sm"
                >
                  {(() => {
                    const tit = BMAB_TITLES.find((x) => x.code === editFields.bmabTitle);
                    return tit ? `${bmabTitleLabel(tit.code, tit.label, tc)} (${tit.code})` : nonePlaceholder;
                  })()}
                </p>
              )}
            </div>
          )}

          {/* Story 096 — FIDE rating-grade fields (chess only). */}
          {gameId === 'chess' && (
            <>
            {editing && (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="detail-fide-lookup-button"
                  disabled={fideAutofill.looking}
                  onClick={lookupFide}
                >
                  <Search className="mr-1 h-4 w-4" />
                  {fideAutofill.looking
                    ? t('fide_autofill.looking')
                    : t('fide_autofill.lookup_button')}
                </Button>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('fide_autofill.lookup_hint')}
                </p>
              </div>
            )}
            <div
              data-testid="detail-fide-fields"
              className="grid grid-cols-1 gap-4 sm:grid-cols-3"
            >
              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  {t('players_page.detail_panel.sex_label')}
                </label>
                {editing ? (
                  <select
                    data-testid="detail-edit-sex"
                    value={editFields.sex}
                    onChange={(e) =>
                      setEditFields((prev) => ({ ...prev, sex: e.target.value }))
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
                  >
                    <option value="">{nonePlaceholder}</option>
                    <option value="m">{t('players_page.detail_panel.sex_m')}</option>
                    <option value="f">{t('players_page.detail_panel.sex_f')}</option>
                  </select>
                ) : (
                  <p
                    data-testid="detail-sex"
                    className="rounded border bg-muted/30 px-3 py-2 text-sm"
                  >
                    {editFields.sex
                      ? t(`players_page.detail_panel.sex_${editFields.sex}`)
                      : nonePlaceholder}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  {t('players_page.detail_panel.birth_year_label')}
                </label>
                {editing ? (
                  <Input
                    data-testid="detail-edit-birthYear"
                    type="number"
                    value={editFields.birthYear}
                    onChange={(e) =>
                      setEditFields((prev) => ({
                        ...prev,
                        birthYear: e.target.value,
                      }))
                    }
                  />
                ) : (
                  <p
                    data-testid="detail-birthYear"
                    className="rounded border bg-muted/30 px-3 py-2 text-sm"
                  >
                    {editFields.birthYear || nonePlaceholder}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  {t('players_page.detail_panel.chess_title_label')}
                </label>
                {editing ? (
                  <select
                    data-testid="detail-edit-chessTitle"
                    value={editFields.chessTitle}
                    onChange={(e) =>
                      setEditFields((prev) => ({
                        ...prev,
                        chessTitle: e.target.value,
                      }))
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
                  >
                    <option value="">{nonePlaceholder}</option>
                    {FIDE_TITLES.map((tit) => (
                      <option key={tit.code} value={tit.code}>
                        {fideTitleLabel(tit.code, tit.label, tc)} ({tit.code})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p
                    data-testid="detail-chessTitle"
                    className="rounded border bg-muted/30 px-3 py-2 text-sm"
                  >
                    {editFields.chessTitle || nonePlaceholder}
                  </p>
                )}
              </div>
            </div>
            </>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              {t('players_page.detail_panel.notes_label')}
            </label>
            {editing ? (
              <Textarea
                data-testid="detail-edit-notes"
                value={editFields.notes}
                onChange={(e) =>
                  setEditFields((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                placeholder={t('players_page.detail_panel.notes_placeholder')}
                rows={3}
              />
            ) : (
              <p
                data-testid="detail-notes"
                className="rounded border bg-muted/30 px-3 py-2 text-sm"
              >
                {editFields.notes || nonePlaceholder}
              </p>
            )}
          </div>
        </div>
      </div>

      <PlayerDetailFooter
        player={player}
        canEdit={canEdit}
        isRegistrationOpen={isRegistrationOpen}
        editing={editing}
        onEnterEdit={() => setEditing(true)}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onStatusChange={onStatusChange}
        onInvite={onInvite}
        onRefreshFromProfile={onRefreshFromProfile}
        onMessage={onMessage}
        onUnblock={onUnblock}
      />

      <FideAutofillDialog
        conflicts={fideAutofill.conflicts}
        i18nNamespace="director"
        onOverwrite={fideAutofill.confirmOverwrite}
        onCancel={fideAutofill.dismiss}
      />
      </div>
    </div>
  );
}
